import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, Calendar, User, ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import logo from "@/assets/logo.jpg";
import SocialShareButtons from "@/components/SocialShareButtons";
import { LanguageSelector } from "@/components/LanguageSelector";
import RotatingNewsBanner from "@/components/RotatingNewsBanner";

interface BlogArticleLayoutProps {
  title: string;
  category: string;
  date: string;
  readTime: string;
  author?: string;
  heroImage?: string;
  heroAlt?: string;
  customHero?: React.ReactNode;
  children: React.ReactNode;
}

const BlogArticleLayout = ({
  title,
  category,
  date,
  readTime,
  author = "Call24x7 Team",
  heroImage,
  heroAlt,
  customHero,
  children,
}: BlogArticleLayoutProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';
  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <RotatingNewsBanner />
      <header className={`fixed top-8 left-0 right-0 z-50 bg-background/95 backdrop-blur-md shadow-md`}>
        <div className="container mx-auto px-2 sm:px-4 py-4">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Link to="/" className={`flex items-center gap-2 hover:opacity-80 transition-opacity ${isRTL ? 'flex-row-reverse' : ''}`}>
              <img src={logo} alt="Call24x7 Logo" className="h-8 w-8" />
              <span className="text-xl font-bold text-primary">Call24x7.AI</span>
            </Link>
            <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <LanguageSelector />
              <Button
                variant="ghost"
                size="sm"
                className="hidden sm:inline-flex"
                onClick={() => (window.location.href = "https://manage.call24x7.ai/login")}
              >
                {t("header.login")}
              </Button>
              <Button
                size="sm"
                onClick={() => (window.location.href = "https://manage.call24x7.ai/signup")}
              >
                {t("header.signUp")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <article className="pt-32 pb-12 px-2 sm:px-4">
        <div className="container mx-auto max-w-3xl">
          <Link
            to="/blog"
            className={`inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <BackArrow className="w-4 h-4" />
            <span>{t("blog.backToBlog").replace('←', '').replace('→', '').trim()}</span>
          </Link>

          {customHero ? (
            <div className="mb-8">{customHero}</div>
          ) : heroImage ? (
            <div className="aspect-video rounded-xl overflow-hidden mb-8">
              <img
                src={heroImage}
                alt={heroAlt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ) : null}

          <Badge className="mb-4">{category}</Badge>

          <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${isRTL ? 'text-right' : ''}`}>{title}</h1>

          <div className={`flex items-center gap-4 text-sm text-muted-foreground mb-8 flex-wrap ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Calendar className="w-4 h-4" />
              {date}
            </span>
            <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Clock className="w-4 h-4" />
              {readTime}
            </span>
            <span className={`flex items-center gap-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <User className="w-4 h-4" />
              {author}
            </span>
          </div>

          <div className={`my-8 py-6 border-y border-border ${isRTL ? 'text-right' : ''}`}>
            <p className="text-sm font-medium text-foreground mb-3">{t("blog.shareArticle")}</p>
            <SocialShareButtons title={title} text={`${t("blog.shareArticle")}: ${title}`} />
          </div>

          <div className={`prose prose-lg max-w-none ${isRTL ? 'prose-rtl text-right' : ''}`}>{children}</div>

          <div className={`mt-10 pt-6 border-t border-border ${isRTL ? 'text-right' : 'text-center'}`}>
            <p className={`text-sm text-muted-foreground mb-4 ${isRTL ? 'text-right' : 'text-center'}`}>{t("blog.shareArticle")}</p>
            <SocialShareButtons title={title} text={`${t("blog.shareArticle")}: ${title}`} />
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogArticleLayout;

